import React, { useEffect } from 'react';
import auth0 from 'auth0-js';
import { useNavigate } from 'react-router-dom';
import App from '../app/app.component';
import { setSentryUser, addAuthBreadcrumb, setSentryTags } from '../sentry';
import logger from '../utils/logger';
const AUTH_EXPIRES_AT_KEY = "AUTH_EXPIRES_AT";
const AUTH_ID_TOKEN_KEY = "AUTH_ID_TOKEN";
const AUTH_ACCESS_TOKEN_KEY = "AUTH_ACCESS_TOKEN";
const AUTH_PACIFY_ROLE_KEY = "AUTH_PACIFY_ROLE";
const AUTH_PERMISSIONS_KEY = "AUTH_PERMISSIONS";
const AUTH_USER_PROFILE_KEY = "AUTH_USER_PROFILE";
const AUTH_USER_SUB_KEY = "AUTH_USER_SUB";
const AUTH_USER_EMAIL_KEY = "AUTH_USER_EMAIL";
const REFRESH_TOKEN_IF_EXPIRING_SOONER_THAN_SECONDS = 1800;
const CONTINUE_URI_QUERY_PARAM_NAME = "continueuri";

const CALLBACK_URL = import.meta.env.VITE_AUTH0_LOGIN_CALLBACK_URL;
const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN;//"pacify.auth0.com";
const AUTH0_CLIENT_ID =
  import.meta.env.VITE_AUTH0_CLIENT_ID ||
  import.meta.env.REACT_APP_AUTH0_CLIENT_ID;


const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
      // [::1] is the IPv6 localhost address.
      window.location.hostname === '[::1]' ||
      // 127.0.0.1/8 is considered localhost for IPv4.
      window.location.hostname.match(
          /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
      )
);


class Auth {
  accessToken;
  idToken;
  expiresAt;
  pacifyRole;
  navigate;
  renewSessionTimeout = null;
  _isHandlingAuth = false; // Guard to prevent double parsing
  _isLoggingOut = false;

  auth0 = new auth0.WebAuth({
    domain: AUTH0_DOMAIN,
    clientID: AUTH0_CLIENT_ID,
    redirectUri: CALLBACK_URL,
    responseType: 'token id_token',
    scope: 'openid'
  });

  constructor() {
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.getIdToken = this.getIdToken.bind(this);
    this.getPacifyRole = this.getPacifyRole.bind(this);
    this.getPermissions = this.getPermissions.bind(this);
    this.renewSession = this.renewSession.bind(this);
    this.cleanup = this.cleanup.bind(this);

    this.expiresAt = localStorage.getItem(AUTH_EXPIRES_AT_KEY);
    this.accessToken = localStorage.getItem(AUTH_ACCESS_TOKEN_KEY);
    this.idToken = localStorage.getItem(AUTH_ID_TOKEN_KEY);

    // Restore Sentry user context if user is authenticated
    if (this.isAuthenticated()) {
      const userEmail = localStorage.getItem(AUTH_USER_EMAIL_KEY);
      const userSub = localStorage.getItem(AUTH_USER_SUB_KEY);

      if (userSub) {
        setSentryUser({
          id: userSub,
          email: userEmail || undefined // Use email for admin users when available
        });

        // Get role safely, handling potential NaN values
        const role = this.getPacifyRole();
        const roleString = (role && !isNaN(role)) ? role.toString() : 'unknown';

        // Set admin-specific tags
        setSentryTags({
          user_type: 'admin',
          app: 'admin-panel',
          authenticated: 'true',
          role: roleString,
          environment: import.meta.env.VITE_CURRENT_ENVIRONMENT || 'unknown'
        });
      }
    } else {
      // Set unauthenticated tags
      setSentryTags({
        user_type: 'anonymous',
        app: 'admin-panel',
        authenticated: 'false',
        environment: import.meta.env.VITE_CURRENT_ENVIRONMENT || 'unknown'
      });
    }
  }

  cleanup() {
    if (this.renewSessionTimeout) {
      clearTimeout(this.renewSessionTimeout);
      this.renewSessionTimeout = null;
    }
  }

  setNavigate(navigate) {
    this.navigate = navigate;
  }

  login() {
    // Reset logout flag when user initiates new login
    this._isLoggingOut = false;

    const currentLocation = window.location.toString();
    const redirectUri = currentLocation.includes('/login') ?
      CALLBACK_URL :
      CALLBACK_URL + "?" + CONTINUE_URI_QUERY_PARAM_NAME + "=" + currentLocation;

    // logger.log('[Auth] Initiating login with redirect URI:', redirectUri);

      this.auth0.authorize({
        scope: 'openid profile',
        redirectUri: redirectUri,
        prompt: 'login'
      });
  }

  handleAuthentication(updateRole) {
    // Prevent double parsing of the authentication hash
    if (this._isHandlingAuth) {
      logger.log('[Auth0] Already handling authentication, skipping duplicate call');
      return;
    }

    // Check if there's actually a hash to parse
    if (!window.location.hash) {
      logger.log('[Auth0] No hash present in URL, skipping parseHash');
      this.navigate('/', { replace: true });
      return;
    }

    this._isHandlingAuth = true;
    logger.log('[Auth0] Handling authentication callback');
    // logger.log('[Auth0] Current URL:', window.location.href);
    // logger.log('[Auth0] Hash:', window.location.hash);

    this.auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        // logger.log('[Auth0] Auth result:', JSON.stringify(authResult));

        // Clear logout flag on successful authentication
        this._isLoggingOut = false;

        this.setSession(authResult);
        updateRole(this.getPacifyRole());

        // After successful authentication, remove the hash from the URL
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        // Get the original URL user was trying to access
        const urlParams = new URLSearchParams(window.location.search);
        const continueuri = urlParams.get(CONTINUE_URI_QUERY_PARAM_NAME);

        // logger.log('[Auth0] Continue URI:', continueuri);

        if (continueuri && !continueuri.includes("login") && !continueuri.includes("callback")) {
          // logger.log('[Auth0] Redirecting to:', continueuri);
          window.location.href = continueuri;
        } else {
          // logger.log('[Auth0] Redirecting to root');
          this.navigate('/', { replace: true });
        }

        // Reset the guard after successful authentication
        this._isHandlingAuth = false;
      } else if (err) {
        logger.error('[Auth0] Error parsing hash:', err);
        this.navigate('/', { replace: true });
        // logger.log('[Auth0] Handle auth error:', err);
        alert(`Error: ${err.error}. Check the console for further details.`);

        // Reset the guard after error
        this._isHandlingAuth = false;
      }
    });
  }

  handleRoute() {
    // All users now use the main admin panel app
    return <App />;
  }

  getAccessToken() {
    return this.accessToken;
  }

  getIdToken() {
    return this.idToken;
  }

  getPacifyRole() {
    let roleFromStorage = localStorage.getItem(AUTH_PACIFY_ROLE_KEY);
    const role = parseInt(roleFromStorage);
    return isNaN(role) ? null : role;
  }

  getPermissions() {
    const permissionsFromStorage = localStorage.getItem(AUTH_PERMISSIONS_KEY);
    try {
      const permissions = JSON.parse(permissionsFromStorage);
      return Array.isArray(permissions) ? permissions : [];
    } catch (e) {
      return [];
    }
  }

  async fetchAndStorePermissions() {
    try {
      const V2_API_URL = import.meta.env.VITE_API_V2_URL || 'http://localhost:7002/v2/api';
      // Fetch permissions from DB-backed endpoint to avoid JWT truncation issues
      const response = await fetch(`${V2_API_URL}/users/me/permissions`, {
        headers: {
          'Authorization': `Bearer ${this.idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const permissions = data.permissions || [];
        localStorage.setItem(AUTH_PERMISSIONS_KEY, JSON.stringify(permissions));
        console.log('[Auth0] Permissions fetched from backend and stored:', permissions);
      } else {
        console.warn('[Auth0] Failed to fetch user permissions from backend:', response.status);
        localStorage.setItem(AUTH_PERMISSIONS_KEY, JSON.stringify([]));
      }
    } catch (error) {
      console.error('[Auth0] Error fetching user permissions:', error);
      localStorage.setItem(AUTH_PERMISSIONS_KEY, JSON.stringify([]));
    }
  }

  setSession(authResult) {
    /* logger.log('[Auth] Setting session with auth result:', {
      exp: authResult.idTokenPayload.exp,
      accessToken: authResult.accessToken ? 'present' : 'missing',
      idToken: authResult.idToken ? 'present' : 'missing'
    });*/

    let expiresAt = authResult.idTokenPayload.exp * 1000 // convert to epoch time in Milliseconds.
    this.expiresAt = expiresAt;
    localStorage.setItem(AUTH_EXPIRES_AT_KEY, this.expiresAt)

    this.accessToken = authResult.accessToken;
    localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, this.accessToken);

    this.idToken = authResult.idToken;
    localStorage.setItem(AUTH_ID_TOKEN_KEY, this.idToken);

    // Store user profile information
    const userProfile = {
      userRole: authResult.idTokenPayload.userRole,
      nickname: authResult.idTokenPayload.nickname,
      name: authResult.idTokenPayload.name,
      picture: authResult.idTokenPayload.picture
    };
    localStorage.setItem(AUTH_USER_PROFILE_KEY, JSON.stringify(userProfile));

    // Store user sub ID for consistent Sentry identification
    localStorage.setItem(AUTH_USER_SUB_KEY, authResult.idTokenPayload.sub);

    // Extract and store user email for Sentry identification
    const userEmail = authResult.idTokenPayload.email || authResult.idTokenPayload.name || null;
    if (userEmail) {
      localStorage.setItem(AUTH_USER_EMAIL_KEY, userEmail);
    }

    // Set Sentry user context with email for admin users
    setSentryUser({
      id: authResult.idTokenPayload.sub,
      email: userEmail || undefined
    });

    // Add authentication breadcrumb
    addAuthBreadcrumb('login', authResult.idTokenPayload.sub);

    // Get role safely for tags
    const currentRole = this.getPacifyRole();
    const roleString = (currentRole && !isNaN(currentRole)) ? currentRole.toString() : 'unknown';

    // Set admin-specific tags
    setSentryTags({
      user_type: 'admin',
      app: 'admin-panel',
      authenticated: 'true',
      role: roleString,
      environment: import.meta.env.VITE_CURRENT_ENVIRONMENT || 'unknown'
    });

    // only for local usage, assign superadmin role (6)
    if (isLocalhost && authResult.idTokenPayload["name"]) {
      this.pacifyRole = 6; // superadmin
    } else {
      this.pacifyRole = authResult.idTokenPayload["https://pacify.com/roleId"];
    }
    localStorage.setItem(AUTH_PACIFY_ROLE_KEY, this.pacifyRole);

    // Store permissions (may need to fetch from backend if JWT is empty due to token size)
    const jwtPermissions = authResult.idTokenPayload["https://pacify.com/permissions"] || [];

    // If permissions are empty in JWT, fetch them from the backend
    if (jwtPermissions.length === 0) {
      this.fetchAndStorePermissions();
    } else {
      localStorage.setItem(AUTH_PERMISSIONS_KEY, JSON.stringify(jwtPermissions));
    }

    // Set up a timer to invoke renewSession even if the tab is in the background
    this.cleanup(); // Clear any existing timeout
    const tenMinutesBeforeExpiryInMS = this.expiresAt - (REFRESH_TOKEN_IF_EXPIRING_SOONER_THAN_SECONDS * 1000) - new Date().getTime();
    this.renewSessionTimeout = setTimeout(this.renewSession, tenMinutesBeforeExpiryInMS);
  }

  renewSession() {
    // Don't renew if we're logging out
    if (this._isLoggingOut) {
      return;
    }

    this.auth0.checkSession({}, (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
      } else if (err) {
        this.logout();
        // logger.log("renew session error: ", err);
      }
    });
  }

  renewSessionIfExpiringSoon() {
    var diffInMS = this.expiresAt - new Date().getTime();
    if (diffInMS < REFRESH_TOKEN_IF_EXPIRING_SOONER_THAN_SECONDS * 1000) {
      this.renewSession();
    }
  }

  logout() {
    // Set flag to prevent silent re-authentication
    this._isLoggingOut = true;

    // Clean up the session renewal timeout
    this.cleanup();

    // Remove tokens and expiry time
    this.accessToken = null;
    localStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);

    this.idToken = null;
    localStorage.removeItem(AUTH_ID_TOKEN_KEY);

    this.expiresAt = 0;
    localStorage.removeItem(AUTH_EXPIRES_AT_KEY);

    this.pacifyRole = 0;
    localStorage.removeItem(AUTH_PACIFY_ROLE_KEY);

    // Clear permissions
    localStorage.removeItem(AUTH_PERMISSIONS_KEY);

    // Clear user profile, sub ID, and email
    localStorage.removeItem(AUTH_USER_PROFILE_KEY);
    localStorage.removeItem(AUTH_USER_SUB_KEY);
    localStorage.removeItem(AUTH_USER_EMAIL_KEY);

    // Clear Sentry user context and set unauthenticated tags
    setSentryUser(null);
    addAuthBreadcrumb('logout');

    // Set unauthenticated tags
    setSentryTags({
      user_type: 'anonymous',
      app: 'admin-panel',
      authenticated: 'false',
      environment: import.meta.env.VITE_CURRENT_ENVIRONMENT || 'unknown'
    });

    // Call Auth0 logout endpoint to invalidate SSO cookie and sign out from Auth0
    const logoutUrl = `https://${AUTH0_DOMAIN}/v2/logout?` +
      `client_id=${AUTH0_CLIENT_ID}&` +
      `returnTo=${encodeURIComponent(window.location.origin)}`;

    logger.log('[Auth0] Logout URL:', logoutUrl);

    // Redirect to Auth0 logout endpoint
    if (typeof window !== 'undefined') {
      window.location.href = logoutUrl;
    }
  }

  isAuthenticated() {
    let expiresAt = this.expiresAt;
    const isAuth = new Date().getTime() < expiresAt && !this._isLoggingOut;
    return isAuth;
  }

  getUserProfile() {
    const profileStr = localStorage.getItem(AUTH_USER_PROFILE_KEY);
    return profileStr ? JSON.parse(profileStr) : null;
  }
}

const auth = new Auth();
export default auth;